import React, { useState, useEffect } from "react";
import axios from "axios";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import { Pie } from "react-chartjs-2";
import "./Dashboard.css";
import { motion } from "framer-motion";
import CircularProgress from "@mui/material/CircularProgress";
import {
  TextField,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { API_BASE_URL } from "Config";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Card from "@mui/material/Card";
import { useMaterialUIController } from "context";
import { useSelector } from "react-redux";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const Dashboard = () => {
  const showSidebar = useSelector((state) => state.sidebar.showSidebar);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branchFilter, setBranchFilter] = useState("");

  useEffect(() => {
    const cid = sessionStorage.getItem("cid");
    const accode = sessionStorage.getItem("accode");

    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/dashboard/get`, {
          params: {
            CID: cid,
            ACCODE: accode,
          },
        });

        setData(response.data);
        setFilteredData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFilterChange = (e) => {
    const filterValue = e.target.value;
    setBranchFilter(filterValue);
    const filtered = data.filter((item) =>
      item.branchname.toLowerCase().includes(filterValue.toLowerCase())
    );
    setFilteredData(filtered);
  };

  const aggregateData = () => {
    let totalDelivered = 0;
    let totalExternalDelay = 0;
    let totalInternalDelay = 0;

    filteredData.forEach((item) => {
      const delivered = parseInt(item.delivered, 10) || 0;
      const externalDelay = parseInt(item.delayexternal, 10) || 0;
      const internalDelay = parseInt(item.delayinternal, 10) || 0;

      totalDelivered += delivered;
      totalExternalDelay += externalDelay;
      totalInternalDelay += internalDelay;
    });

    const total = totalDelivered + totalExternalDelay + totalInternalDelay;

    const chartData = {
      labels: ["Delivered", "External Delay", "Internal Delay"],
      datasets: [
        {
          label: "Delivery Stats",
          data:
            total > 0
              ? [
                  (totalDelivered / total) * 100,
                  (totalExternalDelay / total) * 100,
                  (totalInternalDelay / total) * 100,
                ]
              : [0, 0, 0],
          backgroundColor: ["#4caf50", "#f44336", "#ff9800"],
          hoverBackgroundColor: ["#388e3c", "#d32f2f", "#e65100"],
          borderWidth: 1,
        },
      ],
    };

    return chartData;
  };

  const calculateTotals = () => {
    let totalDelivered = 0;
    let totalExternalDelay = 0;
    let totalInternalDelay = 0;
    let totalDel = 0;

    filteredData.forEach((item) => {
      totalDelivered += parseInt(item.delivered, 10) || 0;
      totalExternalDelay += parseInt(item.delayexternal, 10) || 0;
      totalInternalDelay += parseInt(item.delayinternal, 10) || 0;
      totalDel += parseInt(item.totaldel, 10) || 0;
    });

    return {
      totalDelivered,
      totalExternalDelay,
      totalInternalDelay,
      totalDel,
    };
  };

  const totals = calculateTotals();

  // Theme colors based on darkMode
  const bgColor = darkMode ? "#202940" : "#ffffff";
  const textColor = darkMode ? "#ffffff" : "#344767";
  const borderColor = darkMode ? "#344767" : "#e0e0e0";
  const headerBg = darkMode ? "#344767" : "#f5f5f5";
  const headerColor = darkMode ? "#ffffff" : "#344767";
  const hoverBg = darkMode ? "#2d3a5a" : "#f5f5f5";
  const inputBg = darkMode ? "#344767" : "#ffffff";
  const inputColor = darkMode ? "#ffffff" : "#344767";
  const inputBorderColor = darkMode ? "#4a5a7a" : "#d0d0d0";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        className={`mt-[24%] sm:mt-10 md:mt-0 lg:mt-0 2xl:mt-0 h-[80%] ${
          showSidebar
            ? "transition-all transition-slowest ease ml-20 lg:ml-[18%]"
            : "transition-all transition-slowest ease"
        }`}
      >
        {loading ? (
          <MDBox display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            {/* Chart Card */}
            <MDBox display="flex" justifyContent="center" mt={4} mb={2} px={2}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                style={{ width: "100%" }}
              >
                <Card
                  sx={{
                    width: "100%",
                    p: 3,
                    borderRadius: 3,
                    boxShadow: 5,
                    bgcolor: darkMode ? "#202940" : "#ffffff",
                    color: darkMode ? "#ffffff" : "#344767",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <MDTypography
                    variant="h5"
                    fontWeight="medium"
                    sx={{
                      color: darkMode ? "#ffffff" : "#344767",
                      mb: 2,
                    }}
                  >
                    Total Delivery Distribution
                  </MDTypography>

                  <hr
                    style={{
                      height: "1px",
                      backgroundColor: borderColor,
                      border: "none",
                      marginBottom: "20px",
                    }}
                  />

                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    sx={{ height: 350, width: "100%" }}
                  >
                    <Pie
                      data={aggregateData()}
                      options={{
                        plugins: {
                          tooltip: {
                            callbacks: {
                              label: function (context) {
                                const label = context.label || "";
                                const value = context.raw.toFixed(2);
                                return `${label}: ${value}%`;
                              },
                            },
                          },
                          title: {
                            display: true,
                            text: "Delivery Performance Overview",
                            color: textColor,
                            font: {
                              size: 18,
                            },
                          },
                        },
                        legend: {
                          labels: {
                            color: textColor,
                          },
                        },
                      }}
                    />
                  </MDBox>
                </Card>
              </motion.div>
            </MDBox>

            {/* Table Card */}
            <MDBox display="flex" justifyContent="center" mt={4} px={2}>
              <MDBox width={{ xs: "100%", sm: "100%", md: "100%", lg: "100%" }}>
                <Card
                  sx={{
                    width: "100%",
                    p: 3,
                    borderRadius: 3,
                    boxShadow: 5,
                    bgcolor: darkMode ? "#202940" : "#ffffff",
                    color: darkMode ? "#ffffff" : "#344767",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: 8,
                    },
                  }}
                >
                  <MDTypography
                    variant="h5"
                    fontWeight="medium"
                    sx={{
                      color: darkMode ? "#ffffff" : "#344767",
                      mb: 2,
                    }}
                  >
                    Delivery Details
                  </MDTypography>

                  <hr
                    style={{
                      height: "1px",
                      backgroundColor: borderColor,
                      border: "none",
                      marginBottom: "20px",
                    }}
                  />

                  <MDBox mb={3}>
                    <TextField
                      label="Filter by Branch"
                      variant="outlined"
                      value={branchFilter}
                      onChange={handleFilterChange}
                      size="small"
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: inputBg,
                          color: inputColor,
                          "& fieldset": {
                            borderColor: inputBorderColor,
                          },
                          "&:hover fieldset": {
                            borderColor: darkMode ? "#6a7a9a" : "#999999",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: darkMode ? "#aabbcc" : "#666666",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: darkMode ? "#90caf9" : "#1976d2",
                        },
                        "& .MuiInputBase-input": {
                          color: inputColor,
                        },
                      }}
                    />
                  </MDBox>

                  <TableContainer
                    component={Paper}
                    sx={{
                      backgroundColor: bgColor,
                      boxShadow: "none",
                      border: `1px solid ${borderColor}`,
                      borderRadius: "8px",
                      width: "100%",
                      overflowX: "auto",
                    }}
                  >
                    <Table
                      sx={{
                        backgroundColor: bgColor,
                        width: "100%",
                        tableLayout: "fixed",
                        "& .MuiTableCell-root": {
                          color: textColor,
                          borderColor: borderColor,
                          fontSize: "11px",
                          padding: "6px 8px",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      <TableRow sx={{ backgroundColor: headerBg }}>
                        <TableCell
                          sx={{
                            color: headerColor,
                            fontWeight: "bold",
                            fontSize: "11px",
                            padding: "6px 8px",
                            textAlign: "center",
                            width: "20%",
                          }}
                        >
                          BRANCH NAME
                        </TableCell>
                        <TableCell
                          sx={{
                            color: headerColor,
                            fontWeight: "bold",
                            fontSize: "11px",
                            padding: "6px 8px",
                            textAlign: "center",
                            width: "20%",
                          }}
                        >
                          DELIVERED
                        </TableCell>
                        <TableCell
                          sx={{
                            color: headerColor,
                            fontWeight: "bold",
                            fontSize: "11px",
                            padding: "6px 8px",
                            textAlign: "center",
                            width: "20%",
                          }}
                        >
                          EXTERNAL DELAY
                        </TableCell>
                        <TableCell
                          sx={{
                            color: headerColor,
                            fontWeight: "bold",
                            fontSize: "11px",
                            padding: "6px 8px",
                            textAlign: "center",
                            width: "20%",
                          }}
                        >
                          INTERNAL DELAY
                        </TableCell>
                        <TableCell
                          sx={{
                            color: headerColor,
                            fontWeight: "bold",
                            fontSize: "11px",
                            padding: "6px 8px",
                            textAlign: "center",
                            width: "20%",
                          }}
                        >
                          TOTAL DELIVERIES
                        </TableCell>
                      </TableRow>

                      <TableBody>
                        {/* Data Rows */}
                        {filteredData.length > 0
                          ? filteredData.map((item, index) => (
                              <motion.tr
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                style={{
                                  backgroundColor: bgColor,
                                  color: textColor,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = hoverBg;
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = bgColor;
                                }}
                              >
                                <TableCell
                                  sx={{
                                    color: textColor,
                                    fontSize: "11px",
                                    padding: "6px 8px",
                                    textAlign: "center",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {item.branchname}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: textColor,
                                    fontSize: "11px",
                                    padding: "6px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.delivered}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: textColor,
                                    fontSize: "11px",
                                    padding: "6px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.delayexternal}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: textColor,
                                    fontSize: "11px",
                                    padding: "6px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.delayinternal}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    color: textColor,
                                    fontSize: "11px",
                                    padding: "6px 8px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.totaldel}
                                </TableCell>
                              </motion.tr>
                            ))
                          : null}

                        {/* Total Row - Always visible */}
                        <TableRow
                          sx={{
                            backgroundColor: darkMode ? "#344767" : "#f5f5f5",
                            fontWeight: "bold",
                          }}
                        >
                          <TableCell
                            sx={{
                              color: textColor,
                              fontWeight: "bold",
                              fontSize: "11px",
                              padding: "6px 8px",
                              textAlign: "center",
                            }}
                          >
                            Total
                          </TableCell>
                          <TableCell
                            sx={{
                              color: textColor,
                              fontWeight: "bold",
                              fontSize: "11px",
                              padding: "6px 8px",
                              textAlign: "center",
                            }}
                          >
                            {totals.totalDelivered}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: textColor,
                              fontWeight: "bold",
                              fontSize: "11px",
                              padding: "6px 8px",
                              textAlign: "center",
                            }}
                          >
                            {totals.totalExternalDelay}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: textColor,
                              fontWeight: "bold",
                              fontSize: "11px",
                              padding: "6px 8px",
                              textAlign: "center",
                            }}
                          >
                            {totals.totalInternalDelay}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: textColor,
                              fontWeight: "bold",
                              fontSize: "11px",
                              padding: "6px 8px",
                              textAlign: "center",
                            }}
                          >
                            {totals.totalDel}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </MDBox>
            </MDBox>
          </>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default Dashboard;
